import React, { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { Link, useNavigate, useLocation } from 'react-router-dom'

import { alpha, styled } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
// import useMediaQuery from '@mui/material/useMediaQuery'

import DownIcon from '@mui/icons-material/KeyboardArrowDown'
import UpIcon from '@mui/icons-material/KeyboardArrowUp'

import HelpIcon from '@mui/icons-material/Help'
import HomeIcon from '@mui/icons-material/Home'
import LogoutIcon from '@mui/icons-material/Logout'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import ProgressIcon from '@mui/icons-material/EmojiEvents'
import ScheduleIcon from '@mui/icons-material/Schedule'

import {
  AdminIcon,
  AssignmentIcon,
  CloseIcon,
  CodexIcon,
  CreatorIcon,
  CredentialIcon,
  DashboardIcon,
  GroupIcon,
  LinkedServiceIcon,
  ConnectClientsIcon,
  LiveClassIcon,
  ManagerIcon,
  MenuIcon,
  MotherIcon,
  NotificationIcon,
  EventIcon,
  SearchIcon,
  StudentIcon,
  CertificateIcon,
  LearningPathIcon,
  IBSCRenewalIcon,
  TemplateIcon,
  SpecialCertificationIcon,
  RosterIcon,
  TrainingSeriesIcon,
  SeriesIcon,
  CourseIcon,
  VideoIcon,
  SlideshowIcon,
  ProfileIcon,
  ScribeIcon,
  AnalyticsIcon,
  QAIcon,
  EditIcon,
  QuizIcon,
  ShowNotesIcon,
  VerifyIcon,
  ViewStatsIcon,
  AccreditationIcon,
  RecertificationIcon,
} from 'src/v3/core/icons'
import {
  fetchNotifications,
  selectNotifications,
  selectNoficationsStatus,
  selectUseEvents,
  selectUseCreateV5,
  fetchUser,
  selectUser,
  selectUserStatus,
  fetchProfileImage,
  selectProfileImage,
  selectProfileImageStatus,
  selectSettings,
  selectSettingsStatus,
  fetchSettings,
  selectUseProfileAssignmentsv4,
  selectUseIBSCV2,
  fetchMaintenanceConfig,
  selectMaintenanceConfigStatus,
  selectUseEventsManagerDashboard,
  selectUseEventsStudentView,
  selectUseRecertificationCycle,
  selectUseQuizAnalyticsV2,
} from 'src/v3/settings/settings.slice'
import { useAuth0 } from '@auth0/auth0-react'
import axios from 'src/utilities/http-client'
import globalTheme from 'src/theme'
import {
  MaintenanceBanner,
  MaintenanceToast,
} from './maintenance/maintenance-banner'
import { useMaintenanceTimer } from './maintenance/use-maintenance-timer'
import {
  selectBoundTreeStatus,
  selectRoles,
  selectNavigationStatus,
  navigationStatusChanged,
} from '../settings/settings.slice'
import {
  fetchTourCompletions,
  selectTourCompletionsStatus,
  selectActiveTourID,
} from '../tours/tours.slice'
import { SIDEBAR_NAVIGATION_TOUR_ID } from '../tours/definitions/sidebar-navigation.tour'

let drawerWidth = 260

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
}>(({ theme }) => ({
  flexGrow: 1,
  // padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
    },
  ],
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  borderBottom: `1px solid ${theme.palette.background.paper}`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: '100%',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

type MenuItem = {
  name: string
  route: string
  icon: React.JSX.Element
  disabled?: boolean
  subMenuItems?: MenuItem[]
  roles?: string[]
  testid?: string
  divider?: boolean
  allowInLimited?: boolean
  flag?: boolean
  dataTour?: string
}

interface Props {
  children: ReactElement | ReactElement[]
  roles?: string[]
}

let Layout = ({ children, roles: roleList }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { pathname, search } = useLocation()
  let fullPath = pathname + search
  let { logout } = useAuth0()

  let boundTreeStatus = useAppSelector(selectBoundTreeStatus)
  let roles = useAppSelector(selectRoles)
  let useEvents = useAppSelector(selectUseEvents)
  let useCreateV5 = useAppSelector(selectUseCreateV5)
  let useProfileAssignmentsv4 = useAppSelector(selectUseProfileAssignmentsv4)
  let useIBSCV2 = useAppSelector(selectUseIBSCV2)
  let useEventsManagerDashboard = useAppSelector(
    selectUseEventsManagerDashboard,
  )
  let useEventsStudentView = useAppSelector(selectUseEventsStudentView)
  let useRecertificationCycle = useAppSelector(selectUseRecertificationCycle)
  let useQuizAnalyticsV2 = useAppSelector(selectUseQuizAnalyticsV2)

  let settings = useAppSelector(selectSettings)
  let settingsStatus = useAppSelector(selectSettingsStatus)
  let user = useAppSelector(selectUser)
  let userStatus = useAppSelector(selectUserStatus)
  let profileImage = useAppSelector(selectProfileImage)
  let profileImageStatus = useAppSelector(selectProfileImageStatus)
  let notifications = useAppSelector(selectNotifications)
  let notificationsStatus = useAppSelector(selectNoficationsStatus)
  let navigationStatus = useAppSelector(selectNavigationStatus)
  let maintenanceConfigStatus = useAppSelector(selectMaintenanceConfigStatus)
  let tourCompletionsStatus = useAppSelector(selectTourCompletionsStatus)
  let activeTourID = useAppSelector(selectActiveTourID)
  let maintenanceTimer = useMaintenanceTimer()

  let menuItems: MenuItem[] = [
    {
      name: 'Self-Paced',
      route: '/self-paced',
      icon: <HomeIcon />,
      allowInLimited: true,
      dataTour: 'nav-self-paced',
    },
    {
      name: 'Learning',
      route: '',
      icon: <StudentIcon />,
      subMenuItems: [
        {
          name: 'Continue Watching',
          route: '/self-paced/continue-watching',
          icon: <PlayCircleIcon />,
        },
        {
          name: 'Assignments',
          route: useProfileAssignmentsv4
            ? '/assignments'
            : '/profile/assignments',
          icon: <AssignmentIcon />,
        },
        {
          name: 'Live Classes',
          route: '/live',
          icon: <LiveClassIcon />,
          dataTour: 'nav-live-classes',
        },
        {
          name: 'Events',
          route: '/events',
          icon: <EventIcon />,
          flag: useEvents,
        },
        {
          name: 'Events V2',
          route: '/events/v2',
          icon: <EventIcon />,
          flag: useEventsStudentView,
        },
      ],
    },
    {
      name: 'Progress',
      route: '',
      icon: <ProgressIcon />,
      divider: true,
      dataTour: 'nav-progress',
      subMenuItems: [
        {
          name: 'Recertification',
          route: '/profile/recertification',
          icon: <RecertificationIcon />,
          flag: useRecertificationCycle,
        },
        {
          name: 'Learning Paths',
          route: '/profile/learning-paths',
          icon: <LearningPathIcon />,
          dataTour: 'nav-learning-paths',
        },
        {
          name: 'NY Learning Paths',
          route: '/profile/new-york-learning-paths',
          icon: <LearningPathIcon />,
          flag: settings?.isNewYork,
        },
        ...(useIBSCV2
          ? [
              {
                name: 'IBSC 40-Hour',
                route: '/profile/ibsc?program=ibsc40',
                icon: <IBSCRenewalIcon />,
                dataTour: 'nav-ibsc-40',
              },
              {
                name: 'IBSC 100-Hour',
                route: '/profile/ibsc?program=ibsc100',
                icon: <IBSCRenewalIcon />,
                dataTour: 'nav-ibsc-100',
              },
            ]
          : [
              {
                name: 'IBSC 100-Hour',
                route: '/profile/ibsc-100',
                icon: <IBSCRenewalIcon />,
                dataTour: 'nav-ibsc-100',
              },
              {
                name: 'IBSC 40-Hour',
                route: '/profile/ibsc-40',
                icon: <IBSCRenewalIcon />,
                dataTour: 'nav-ibsc-40',
              },
            ]),
      ],
    },
    {
      name: 'Admin',
      roles: ['admin'],
      route: '',
      icon: <AdminIcon />,
      subMenuItems: [
        {
          name: 'Dashboard',
          route: '/admin',
          icon: <DashboardIcon />,
        },
        {
          name: 'Users',
          route: '/admin/users',
          icon: <GroupIcon />,
        },
        {
          name: 'Linked Services',
          route: '/admin/linked-services',
          icon: <LinkedServiceIcon />,
        },
        {
          name: 'FOAMfrat Connect Clients',
          route: '/admin/foamfrat-connect-clients',
          icon: <ConnectClientsIcon />,
        },
        {
          name: 'Live Classes',
          route: '/admin/live',
          icon: <LiveClassIcon />,
        },
        {
          name: 'Templates',
          route: '/admin/live/templates',
          icon: <TemplateIcon />,
        },
        {
          name: 'Special Certifications',
          route: '/admin/special-certifications',
          icon: <SpecialCertificationIcon />,
        },
        {
          name: 'MOTHER Audit',
          route: '/admin/mother',
          icon: <MotherIcon />,
        },
      ],
    },
    {
      name: 'Manage',
      roles: ['manager'],
      route: '',
      icon: <ManagerIcon />,
      subMenuItems: [
        {
          name: 'Dashboard',
          route: '/manage',
          icon: <DashboardIcon />,
        },
        {
          name: 'CE Analytics',
          route: '/manage/analytics/ce',
          icon: <AnalyticsIcon />,
        },
        {
          name: 'Quiz Analytics',
          route: '/manage/quiz-analytics',
          icon: <AnalyticsIcon />,
          flag: useQuizAnalyticsV2,
        },
        {
          name: 'Linked Service',
          route: '/manage/linked-service',
          icon: <LinkedServiceIcon />,
        },
        {
          name: 'Roster',
          route: '/manage/roster',
          icon: <RosterIcon />,
        },
        {
          name: 'Assignments',
          route: '/manage/assignments',
          icon: <AssignmentIcon />,
        },
        {
          name: 'Scheduled Assignments',
          route: '/manage/scheduled-assignments',
          icon: <ScheduleIcon />,
        },
        {
          name: 'Compliance',
          route: '/manage/compliance',
          icon: <VerifyIcon />,
        },
        {
          name: 'Training Series',
          route: '/manage/training-series',
          icon: <TrainingSeriesIcon />,
        },
        {
          name: 'Groups',
          route: '/manage/groups',
          icon: <GroupIcon />,
        },
        {
          name: 'Events',
          route: '/manage/events',
          icon: <EventIcon />,
          flag: useEventsManagerDashboard,
        },
      ],
    },
    {
      name: 'Create',
      roles: ['creator'],
      route: '',
      icon: <CreatorIcon />,
      subMenuItems: [
        {
          name: 'Courses',
          route: '/create/v4',
          icon: <CourseIcon />,
          flag: !useCreateV5,
        },
        {
          name: 'Courses',
          route: '/create/v5/course',
          icon: <CourseIcon />,
          flag: useCreateV5,
        },
        {
          name: 'Templates',
          route: '/create/v4/templates',
          icon: <TemplateIcon />,
        },
        {
          name: 'Learning Paths',
          route: '/create/v4/learning-paths',
          icon: <LearningPathIcon />,
        },
        {
          name: 'Series',
          route: '/create/v4/series',
          icon: <SeriesIcon />,
          roles: ['admin'],
        },
        {
          name: 'Series V5',
          route: '/create/v5/series',
          icon: <SeriesIcon />,
          roles: ['admin'],
          flag: useCreateV5,
        },
        {
          name: 'Marketing',
          route: '/create/v4/marketing-slideshows',
          icon: <SlideshowIcon />,
          roles: ['admin'],
        },
        {
          name: 'Video Library',
          route: '/create/v5/video-library',
          icon: <VideoIcon />,
          flag: useCreateV5,
        },
        {
          name: 'Show Notes Library',
          route: '/create/v5/show-notes-library',
          icon: <ShowNotesIcon />,
          flag: useCreateV5,
        },
        {
          name: 'Quiz Library',
          route: '/create/v5/quiz-library',
          icon: <QuizIcon />,
          flag: useCreateV5,
        },
      ],
    },
    {
      name: 'Content Pipeline',
      roles: [
        'content-compliance',
        'content-liaison',
        'content-planner',
        'content-analyzer',
        'content-instructor',
      ],
      route: '',
      icon: <QAIcon />,
      subMenuItems: [
        {
          name: 'Planning',
          route: '/pipeline/planning',
          icon: <EditIcon />,
          roles: ['content-planner'],
        },
        {
          name: 'Compliance',
          route: '/pipeline/compliance',
          icon: <VerifyIcon />,
          roles: ['content-compliance'],
        },
        {
          name: 'Performance',
          route: '/pipeline/performance',
          icon: <ViewStatsIcon />,
          roles: ['content-analyzer'],
        },
        {
          name: 'Accreditation',
          route: '/pipeline/accreditation',
          icon: <AccreditationIcon />,
          roles: ['content-liaison'],
        },
        {
          name: 'Instructor',
          route: '/pipeline/instructor',
          icon: <RosterIcon />,
          roles: ['content-instructor'],
        },
      ],
    },
    {
      name: 'Scribe',
      roles: ['scribe'],
      route: '',
      icon: <ScribeIcon />,
      subMenuItems: [
        {
          name: 'User Demographics',
          route: '/scribe',
          icon: <AnalyticsIcon />,
        },
        {
          name: 'Sentiment Analysis',
          route: '/scribe/sentiment-analysis',
          icon: <AnalyticsIcon />,
        },
        {
          name: 'CAPCE Submission Analytics',
          route: '/scribe/capce-submission-report',
          icon: <AnalyticsIcon />,
        },
        {
          name: 'Auth Login Analytics',
          route: '/scribe/auth-login-report',
          icon: <AnalyticsIcon />,
        },
        {
          name: 'MOTHER',
          route: '/scribe/mother',
          icon: <AnalyticsIcon />,
          roles: ['developer'],
        },
      ],
    },
    {
      name: 'Codex',
      roles: ['codex-editor'],
      route: '',
      icon: <CodexIcon />,
      subMenuItems: [
        {
          name: 'Books',
          route: '/codex/books',
          icon: <TemplateIcon />,
        },
        {
          name: 'Contributors',
          route: '/codex/contributors',
          icon: <RosterIcon />,
        },
      ],
    },
  ]

  let rightMenuItems: MenuItem[] = [
    {
      name: 'Profile Settings',
      route: '/profile',
      icon: <ProfileIcon />,
      allowInLimited: true,
    },
    {
      name: 'Certificates',
      route: '/profile/certificates',
      icon: <CertificateIcon />,
      allowInLimited: true,
    },
    {
      name: 'Credentials',
      route: '/profile/credentials',
      icon: <CredentialIcon />,
    },
    {
      name: 'Recertification',
      route: '/profile/recertification',
      icon: <RecertificationIcon />,
      flag: useRecertificationCycle,
    },
  ]

  // let isTablet = useMediaQuery(globalTheme.breakpoints.down('md'))
  // let isPhone = useMediaQuery(globalTheme.breakpoints.down('sm'))

  // let [menuOpen, setMenuOpen] = useState<boolean>(false)
  let [rightMenuOpen, setRightMenuOpen] = useState<boolean>(false)
  let [notificationsOpen, setNotificationsOpen] = useState<boolean>(false)
  let [notificationDelete, setNotificationDelete] = useState<string>('')

  let unreadNotifications = notifications.filter(
    (n) => n.status === 'unread',
  ).length
  let isBoundtree =
    boundTreeStatus === 'subscribed' || boundTreeStatus === 'limited'

  if (isBoundtree) {
    // eslint-disable-next-line fp/no-mutating-methods
    menuItems.push({
      name: 'Certificates',
      route: '/profile/certificates',
      icon: <CertificateIcon />,
      allowInLimited: true,
    })
  }

  let appBarHeight = 80
  let appBarOffsetLeft = `${appBarHeight - 6}px`
  let appBarOffsetRight = `${appBarHeight - 7}px`

  let menuOpen = navigationStatus.open
  let setMenuOpen = (status: boolean) => {
    dispatch(
      navigationStatusChanged({
        ...navigationStatus,
        open: status,
      }),
    )
    if (status) {
      setRightMenuOpen(false)
    }
  }

  useEffect(() => {
    if (settingsStatus === 'uninitialized') {
      dispatch(fetchSettings())
    }
  }, [settingsStatus])

  useEffect(() => {
    if (userStatus === 'uninitialized') {
      dispatch(fetchUser())
    }
  }, [userStatus])

  useEffect(() => {
    if (profileImageStatus === 'uninitialized') {
      dispatch(fetchProfileImage())
    }
  }, [profileImageStatus])

  useEffect(() => {
    if (notificationsStatus === 'uninitialized') {
      dispatch(fetchNotifications())
    }
  }, [notificationsStatus])

  useEffect(() => {
    if (maintenanceConfigStatus === 'uninitialized') {
      dispatch(fetchMaintenanceConfig())
    }
  }, [maintenanceConfigStatus])

  useEffect(() => {
    if (tourCompletionsStatus === 'uninitialized') {
      dispatch(fetchTourCompletions())
    }
  }, [tourCompletionsStatus])

  // The sidebar navigation tour spotlights items that only render when the drawer
  // is open and the Learning/Progress groups are expanded. Keep them open for the
  // whole tour: re-run whenever the nav state changes so that if the user collapses
  // the drawer or a group mid-tour, later steps don't lose their targets. The guard
  // below stops this from looping once everything is already expanded.
  useEffect(() => {
    if (activeTourID !== SIDEBAR_NAVIGATION_TOUR_ID) return
    if (
      navigationStatus.open &&
      navigationStatus.Learning &&
      navigationStatus.Progress
    )
      return
    dispatch(
      navigationStatusChanged({
        ...navigationStatus,
        open: true,
        Learning: true,
        Progress: true,
      }),
    )
  }, [activeTourID, navigationStatus, dispatch])

  return roleList && !roles?.some((r) => roleList.includes(r)) ? (
    <></>
  ) : (
    <Box sx={{ display: 'flex', background: 'background.default' }}>
      <CssBaseline />
      <AppBar
        // sx={{ backgroundColor: 'background.menu', border: 'none' }}
        position="fixed"
      >
        <Toolbar
          sx={{ backgroundColor: 'background.menu', height: appBarHeight }}
        >
          <IconButton
            data-testid="left-menu-toggle"
            color="inherit"
            aria-label="open left sub menu"
            onClick={() => setMenuOpen(!menuOpen)}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexShrink: 0 }}>
            <Link to="/self-paced">
              <img
                data-testid={isBoundtree ? 'boundtree-logo' : 'logo'}
                src={isBoundtree ? '/btlogo.png' : '/logo.svg'}
                alt="logo"
                width={isBoundtree ? '180px' : '60px'}
                height="60px"
              />
            </Link>
          </Box>
          <Box sx={{ flexGrow: 1 }}></Box>
          {boundTreeStatus !== 'limited' && (
            <IconButton
              aria-label="Search"
              onClick={() => navigate('/search')}
              sx={{
                color: pathname.includes('search') ? '#42a5f5' : '#fff',
              }}
            >
              <SearchIcon />
            </IconButton>
          )}
          <>
            <IconButton
              aria-label="Notifications"
              sx={{
                position: 'relative',
                color: unreadNotifications > 0 ? 'primary.main' : 'white',
              }}
              onClick={() => setNotificationsOpen(!notificationsOpen)}
            >
              <NotificationIcon />
              {unreadNotifications > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    fontSize: 12,
                    top: 1,
                    right: 0,
                    textAlign: 'center',
                    width: 14,
                  }}
                >
                  {unreadNotifications}
                </Box>
              )}
            </IconButton>
            {notificationsOpen && (
              <ClickAwayListener
                onClickAway={() => setNotificationsOpen(false)}
              >
                <List
                  sx={{
                    position: 'absolute',
                    top: '64px',
                    right: 8,
                    py: 0,
                    overflow: 'scroll',
                    maxWidth: 400,
                  }}
                >
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <ListItem
                        key={n.id}
                        sx={{
                          py: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 0.75,
                          bgcolor: 'background.paper',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#333333',
                            borderLeftColor:
                              n.status === 'unread'
                                ? 'primary.main'
                                : '#333333',
                          },
                          borderLeftWidth: 6,
                          borderLeftStyle: 'solid',
                          borderLeftColor:
                            n.status === 'unread'
                              ? 'primary.dark'
                              : 'background.paper',
                        }}
                        onClick={() => {
                          setNotificationsOpen(false)
                          axios
                            .put('/api/settings/notification/status', {
                              id: n.id,
                              status: 'read',
                            })
                            .then(() => dispatch(fetchNotifications()))
                          navigate(n.route)
                        }}
                      >
                        <Typography>{n.message}</Typography>
                        <Box
                          sx={{
                            mr: -1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 34,
                            width: 34,
                          }}
                        >
                          {notificationDelete === n.id ? (
                            <CircularProgress size={24} sx={{ mx: '5px' }} />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation()
                                setNotificationDelete(n.id)
                                axios
                                  .delete(
                                    `/api/settings/notification?notificationID=${n.id}`,
                                  )
                                  .then(() => dispatch(fetchNotifications()))
                                  .finally(() => setNotificationDelete(''))
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          )}
                        </Box>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem
                      sx={{
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#333333',
                        },
                      }}
                      onClick={() => setNotificationsOpen(false)}
                    >
                      You have no notifications.
                    </ListItem>
                  )}
                </List>
              </ClickAwayListener>
            )}
          </>
          <Button
            color="inherit"
            data-testid="right-menu-toggle"
            data-tour="profile-menu-button"
            aria-label="open right sub menu"
            onClick={() => setRightMenuOpen(!rightMenuOpen)}
            sx={{
              ml: 0,
            }}
          >
            <Avatar
              src={profileImage}
              alt={
                user?.name
                  ? `${user.name}'s profile picture`
                  : 'User profile picture'
              }
              sx={{ height: 35, width: 35, marginLeft: 0.5 }}
            />
            <Typography
              sx={{
                ml: 1,
                mr: 0.5,
                textTransform: 'none',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {user?.name}
            </Typography>
            {rightMenuOpen ? <UpIcon /> : <DownIcon />}
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        variant="persistent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'background.menu',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <List sx={{ mt: appBarOffsetLeft, minWidth: drawerWidth }}>
          {menuItems
            .filter(
              (item) =>
                (boundTreeStatus !== 'limited' || item.allowInLimited) &&
                item.flag !== false &&
                (!item.roles || roles.some((r) => item.roles?.includes(r))),
            )
            .map((item) => (
              <React.Fragment key={item.name}>
                <ListItem
                  key={item.name}
                  disablePadding
                  sx={{
                    display: 'block',
                    color: item.route === fullPath ? 'primary.main' : undefined,
                  }}
                >
                  <ListItemButton
                    data-tour={item.dataTour}
                    onClick={() =>
                      !item.subMenuItems
                        ? navigate(item.route)
                        : dispatch(
                            navigationStatusChanged({
                              ...navigationStatus,
                              [item.name]: !navigationStatus[item.name],
                            }),
                          )
                    }
                    sx={{
                      mx: 0.5,
                      my: 0.25,
                      borderRadius: 1.5,
                      bgcolor:
                        item.route === fullPath
                          ? alpha(globalTheme.palette.primary.main, 0.1)
                          : undefined,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 1.5,
                        justifyContent: 'center',
                        color:
                          item.route === fullPath ? 'primary.main' : undefined,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.name} sx={{ mr: 1 }} />
                    {item.subMenuItems?.length &&
                      (navigationStatus[item.name] ? <UpIcon /> : <DownIcon />)}
                  </ListItemButton>
                </ListItem>
                {navigationStatus[item.name] &&
                  item.subMenuItems
                    ?.filter(
                      (subItem) =>
                        subItem.flag !== false &&
                        (!subItem.roles ||
                          roles.some((r) => subItem.roles?.includes(r))),
                    )
                    .map((subItem) => (
                      <ListItem
                        key={subItem.name}
                        disablePadding
                        sx={{
                          display: 'block',
                          color:
                            subItem.route === fullPath
                              ? 'primary.main'
                              : undefined,
                        }}
                      >
                        <ListItemButton
                          data-tour={subItem.dataTour}
                          onClick={() => navigate(subItem.route)}
                          sx={{
                            mx: 0.5,
                            my: 0.25,
                            borderRadius: 1.5,
                            bgcolor:
                              subItem.route === fullPath
                                ? alpha(globalTheme.palette.primary.main, 0.1)
                                : undefined,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              ml: 2,
                              justifyContent: 'center',
                              color:
                                subItem.route === fullPath
                                  ? 'primary.main'
                                  : undefined,
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText primary={subItem.name} sx={{ mr: 0 }} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                {item.divider &&
                  ['admin', 'manager', 'creator', 'scribe'].some((r) =>
                    roles.includes(r),
                  ) && <Divider sx={{ my: 0.5 }} />}
              </React.Fragment>
            ))}
        </List>
      </Drawer>
      <Drawer
        anchor="right"
        open={rightMenuOpen}
        onClose={() => setRightMenuOpen(false)}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'background.default',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <List sx={{ mt: appBarOffsetRight, minWidth: drawerWidth }}>
          <ListItem
            disablePadding
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
            }}
          >
            <Avatar
              src={profileImage}
              alt={
                user?.name
                  ? `${user.name}'s profile picture`
                  : 'User profile picture'
              }
              sx={{ height: 35, width: 35 }}
            />
            <Box>
              <Typography>{user?.name}</Typography>
              <Typography variant="subtitle2">
                {['Admin', 'Manager', 'Creator', 'Scribe', 'Student'].find(
                  (r) => roles.includes(r.toLowerCase()),
                )}
              </Typography>
            </Box>
          </ListItem>
          <Divider sx={{ borderColor: 'divider.default' }} />
          {rightMenuItems
            .filter(
              (item) =>
                (!isBoundtree || item.allowInLimited) && item.flag !== false,
            )
            .map((item) => (
              <ListItem
                key={item.name}
                disablePadding
                sx={{
                  color: item.route === fullPath ? 'primary.main' : undefined,
                }}
              >
                <ListItemButton onClick={() => navigate(item.route)}>
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 1.5,
                      justifyContent: 'center',
                      color:
                        item.route === fullPath ? 'primary.main' : undefined,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.name} sx={{ mr: 0 }} />
                </ListItemButton>
              </ListItem>
            ))}
          <Divider sx={{ borderColor: 'divider.default' }} />
          <ListItem disablePadding>
            <ListItemButton
              href="https://foamfrat.my.site.com/support/s/"
              target="_blank"
            >
              <ListItemIcon
                sx={{ minWidth: 0, mr: 1.5, justifyContent: 'center' }}
              >
                <HelpIcon />
              </ListItemIcon>
              <ListItemText primary="Help" sx={{ mr: 0 }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() =>
                logout({
                  logoutParams: {
                    returnTo: import.meta.env.VITE_AUTH0_LOGOUT_REDIRECT_URI,
                  },
                })
              }
            >
              <ListItemIcon
                sx={{ minWidth: 0, mr: 1.5, justifyContent: 'center' }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Sign Out" sx={{ mr: 0 }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      <div style={{ width: `calc(100vw - ${drawerWidth}px` }}>
        <Main open={menuOpen}>
          <Container
            maxWidth={pathname === '/self-paced' ? false : 'xl'}
            disableGutters
            sx={{
              pt:
                pathname === '/self-paced'
                  ? 10
                  : pathname.startsWith('/classroom-v2')
                    ? 9.5
                    : 12,
              px:
                pathname === '/self-paced' ||
                pathname.startsWith('/classroom-v2')
                  ? 0
                  : 3,
              pb: 3,
            }}
          >
            <MaintenanceBanner timer={maintenanceTimer} />
            {children}
          </Container>
        </Main>
      </div>
      <MaintenanceToast timer={maintenanceTimer} />
    </Box>
  )
}

export let AdminLayout = ({ children }: Props): ReactElement => (
  <Layout roles={['admin']}>{children}</Layout>
)

export let ManagerLayout = ({ children }: Props): ReactElement => (
  <Layout roles={['manager']}>{children}</Layout>
)

export let CreatorLayout = ({ children }: Props): ReactElement => (
  <Layout roles={['creator']}>{children}</Layout>
)

export let ContentPipelineLayout = ({ children }: Props): ReactElement => (
  <Layout
    roles={[
      'content-compliance',
      'content-liaison',
      'content-planner',
      'content-analyzer',
      'content-instructor',
    ]}
  >
    {children}
  </Layout>
)

export let ScribeLayout = ({ children }: Props): ReactElement => (
  <Layout roles={['scribe']}>{children}</Layout>
)

export let CodexLayout = ({ children }: Props): ReactElement => (
  <Layout roles={['codex-editor']}>{children}</Layout>
)

export default Layout
